/**
 * 图层样式渲染（M3-14 / DESIGN.md §4.4）。
 *
 * 先做投影与描边——§11 原表列的第一批。两者都在**alpha 通道**上工作：
 * 描边是形态学环（膨胀/腐蚀的差），投影是位移后的模糊 alpha，
 * 颜色由参数给、写入时再与 RGB 混合。
 *
 * 这里的模糊是**三遍盒式滤波**（同步、无 wasm 依赖）近似高斯——CPU 合成桥
 * 本就是非权威的显示桥（composite.ts 文件头已有此免责声明），效果预览
 * 不需要 GPU 权威实现要求的精确度；真正的高精度实现随 GPU 执行器接入
 * （M3-09）时再替换。
 */

/** 圆形结构元的偏移表（半径 r，欧氏距离 ≤ r）。半径 0 时只含原点。 */
function circleOffsets(radius: number): ReadonlyArray<readonly [number, number]> {
  const r = Math.max(0, Math.round(radius));
  const offsets: Array<[number, number]> = [];
  const r2 = r * r + 0.5; // +0.5 让整数半径下的边界像素被稳定纳入
  for (let dy = -r; dy <= r; dy++) {
    for (let dx = -r; dx <= r; dx++) {
      if (dx * dx + dy * dy <= r2) offsets.push([dx, dy]);
    }
  }
  return offsets;
}

/** 形态学膨胀（取邻域最大值）。越界视为 0（画布外没有内容）。 */
export function dilateAlpha(
  alpha: Uint8Array,
  width: number,
  height: number,
  radius: number,
): Uint8Array {
  if (radius <= 0) return alpha.slice();
  const offsets = circleOffsets(radius);
  const out = new Uint8Array(alpha.length);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      let max = 0;
      for (const [dx, dy] of offsets) {
        const sx = x + dx;
        const sy = y + dy;
        if (sx < 0 || sy < 0 || sx >= width || sy >= height) continue;
        const v = alpha[sy * width + sx]!;
        if (v > max) max = v;
      }
      out[y * width + x] = max;
    }
  }
  return out;
}

/** 形态学腐蚀（取邻域最小值）。越界视为 0——边缘必然被腐蚀掉。 */
export function erodeAlpha(
  alpha: Uint8Array,
  width: number,
  height: number,
  radius: number,
): Uint8Array {
  if (radius <= 0) return alpha.slice();
  const offsets = circleOffsets(radius);
  const out = new Uint8Array(alpha.length);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      let min = 255;
      for (const [dx, dy] of offsets) {
        const sx = x + dx;
        const sy = y + dy;
        const v = sx < 0 || sy < 0 || sx >= width || sy >= height ? 0 : alpha[sy * width + sx]!;
        if (v < min) min = v;
        if (min === 0) break;
      }
      out[y * width + x] = min;
    }
  }
  return out;
}

/** 三遍盒式滤波近似高斯（同步，整数）。clamp-to-edge。 */
export function boxBlurAlpha(
  alpha: Uint8Array,
  width: number,
  height: number,
  radius: number,
): Uint8Array {
  if (radius <= 0) return alpha.slice();
  let current = alpha;
  for (let pass = 0; pass < 3; pass++) {
    current = boxPass(current, width, height, radius);
  }
  return current;
}

function boxPass(src: Uint8Array, width: number, height: number, radius: number): Uint8Array {
  const horizontal = new Float32Array(src.length);
  const size = radius * 2 + 1;
  for (let y = 0; y < height; y++) {
    const row = y * width;
    let sum = 0;
    for (let x = -radius; x <= radius; x++) {
      sum += src[row + clampIndex(x, width)]!;
    }
    for (let x = 0; x < width; x++) {
      horizontal[row + x] = sum / size;
      const dropX = x - radius;
      const addX = x + radius + 1;
      sum += src[row + clampIndex(addX, width)]! - src[row + clampIndex(dropX, width)]!;
    }
  }

  const out = new Uint8Array(src.length);
  for (let x = 0; x < width; x++) {
    let sum = 0;
    for (let y = -radius; y <= radius; y++) {
      sum += horizontal[clampIndex(y, height) * width + x]!;
    }
    for (let y = 0; y < height; y++) {
      out[y * width + x] = Math.round(sum / size);
      const dropY = y - radius;
      const addY = y + radius + 1;
      sum += horizontal[clampIndex(addY, height) * width + x]! - horizontal[clampIndex(dropY, height) * width + x]!;
    }
  }
  return out;
}

function clampIndex(i: number, len: number): number {
  if (i < 0) return 0;
  if (i >= len) return len - 1;
  return i;
}

export interface StrokeParams {
  readonly width: number;
  readonly position: 'inside' | 'outside' | 'center';
}

/** 描边环的 alpha（0–255）。位置决定环相对原始边界的取向。 */
export function renderStroke(
  alpha: Uint8Array,
  width: number,
  height: number,
  params: StrokeParams,
): Uint8Array {
  const w = Math.max(1, Math.round(params.width));
  if (params.position === 'outside') {
    const dilated = dilateAlpha(alpha, width, height, w);
    return subtractAlpha(dilated, alpha);
  }
  if (params.position === 'inside') {
    const eroded = erodeAlpha(alpha, width, height, w);
    return subtractAlpha(alpha, eroded);
  }
  // center：以半宽膨胀、半宽腐蚀，环跨在原边界两侧
  const half = Math.max(1, Math.round(w / 2));
  const dilated = dilateAlpha(alpha, width, height, half);
  const eroded = erodeAlpha(alpha, width, height, half);
  return subtractAlpha(dilated, eroded);
}

function subtractAlpha(a: Uint8Array, b: Uint8Array): Uint8Array {
  const out = new Uint8Array(a.length);
  for (let i = 0; i < a.length; i++) out[i] = Math.max(0, a[i]! - b[i]!);
  return out;
}

export interface DropShadowParams {
  readonly distance: number;
  /**
   * 光源方向角度（度，PS 惯例）：阴影甩向光源的反方向。
   * 0° = 光源在右侧（阴影甩向左）；90° = 光源在上方（阴影甩向下）；
   * 180° = 光源在左侧（阴影甩向右）；270° = 光源在下方（阴影甩向上）。
   */
  readonly angle: number;
  readonly blur: number;
}

/** 按角度+距离位移一份 alpha 缓冲，越界处补 0（画布外没有内容）。 */
function shiftAlpha(alpha: Uint8Array, width: number, height: number, dx: number, dy: number): Uint8Array {
  const shifted = new Uint8Array(alpha.length);
  for (let y = 0; y < height; y++) {
    const sy = y - dy;
    if (sy < 0 || sy >= height) continue;
    for (let x = 0; x < width; x++) {
      const sx = x - dx;
      if (sx < 0 || sx >= width) continue;
      shifted[y * width + x] = alpha[sy * width + sx]!;
    }
  }
  return shifted;
}

/**
 * 光源角度 → 阴影位移向量。阴影甩向光源的反方向，屏幕坐标 y 向下，
 * 所以 x 分量要对标准数学 cos 取反（PS 角度 135° 应得到右下方位移，
 * 验证见 `test/e2e/layer-context-menu.spec.ts` 的投影效果测试）。
 */
function offsetFromAngle(distance: number, angleDeg: number): { readonly dx: number; readonly dy: number } {
  const rad = (angleDeg * Math.PI) / 180;
  return { dx: Math.round(-Math.cos(rad) * distance), dy: Math.round(Math.sin(rad) * distance) };
}

/** 投影的 alpha（已位移+模糊，颜色由调用方混入）。 */
export function renderDropShadow(
  alpha: Uint8Array,
  width: number,
  height: number,
  params: DropShadowParams,
): Uint8Array {
  const { dx, dy } = offsetFromAngle(params.distance, params.angle);
  return boxBlurAlpha(shiftAlpha(alpha, width, height, dx, dy), width, height, params.blur);
}

export interface InnerShadowParams {
  readonly distance: number;
  readonly angle: number;
  readonly blur: number;
}

/**
 * 内阴影的 alpha：光线从 `angle` 方向照来，图层挡光，在内部投出阴影——
 * 跟投影是同一束光的两种效果，阴影方向因此相反（位移用 `-distance`）。
 * 用"反转 alpha 位移后模糊，再夹在原图形内部"来近似：反转 alpha 位移后
 * 的亮区正好对应"光被挡住的那一侧"，跟原 alpha 相交就是内部阴影的范围。
 */
export function renderInnerShadow(
  alpha: Uint8Array,
  width: number,
  height: number,
  params: InnerShadowParams,
): Uint8Array {
  const { dx, dy } = offsetFromAngle(params.distance, params.angle);
  const inverted = new Uint8Array(alpha.length);
  for (let i = 0; i < alpha.length; i++) inverted[i] = 255 - alpha[i]!;
  // 位移方向相反：投影是"影子甩向背光方向"，内阴影是"暗区从迎光边界缩回来"
  const shifted = shiftAlpha(inverted, width, height, -dx, -dy);
  const blurred = boxBlurAlpha(shifted, width, height, params.blur);
  const out = new Uint8Array(alpha.length);
  for (let i = 0; i < alpha.length; i++) out[i] = Math.min(alpha[i]!, blurred[i]!);
  return out;
}

export interface GlowParams {
  readonly blur: number;
}

/**
 * 外发光的 alpha：单纯模糊原 alpha，不位移——合成顺序上外发光画在图层
 * 本体**下面**（跟投影同一相位），图层自己的像素盖在上面，模糊晕开的部分
 * 只会在图形轮廓外露出来，天然就是"外发光"的样子，不需要额外裁剪。
 */
export function renderOuterGlow(alpha: Uint8Array, width: number, height: number, params: GlowParams): Uint8Array {
  return boxBlurAlpha(alpha, width, height, params.blur);
}

/**
 * 内发光的 alpha：从边缘往里发光，用"边缘薄环模糊后夹在图形内部"近似——
 * `renderStroke` 的 inside 变体本来就是一条贴着内边缘的环，模糊它、
 * 再跟原 alpha 取 min 收在图形内部，效果上就是"光从边缘往里晕开"。
 */
export function renderInnerGlow(alpha: Uint8Array, width: number, height: number, params: GlowParams): Uint8Array {
  const edgeBandWidth = Math.max(1, Math.round(params.blur));
  const band = renderStroke(alpha, width, height, { width: edgeBandWidth, position: 'inside' });
  const blurred = boxBlurAlpha(band, width, height, params.blur);
  const out = new Uint8Array(alpha.length);
  for (let i = 0; i < alpha.length; i++) out[i] = Math.min(alpha[i]!, Math.max(band[i]!, blurred[i]!));
  return out;
}

export interface BevelEmbossParams {
  readonly size: number;
  /** 光源角度（度），同投影的角度约定。 */
  readonly angle: number;
  /** 光源高度角（度），0=贴着表面斜射，90=正上方直射。 */
  readonly altitude: number;
}

export interface BevelEmbossResult {
  readonly highlight: Uint8Array;
  readonly shadow: Uint8Array;
}

/**
 * 斜面和浮雕：从模糊过的 alpha 算梯度（中心差分近似表面斜率），跟光源
 * 方向的水平分量点乘得到明暗强度——只取水平分量是关键：平坦区域梯度
 * 恒为 0，因此恒定为"没有效果"，不会像完整漫反射光照那样让整块平面
 * 都笼上一层均匀的高光（第一版量出来的真实 bug）。这是对"凹凸感"最
 * 直接的近似，PS 真实实现还有更多轮廓样式（外斜面/内斜面/浮雕/枕状
 * 浮雕）与光泽等高线，这里只做最基础的"内斜面"效果，是本次新增效果里
 * 跟真实 PS 差距最大的一个（已知的近似，不是 bug）。
 */
export function renderBevelEmboss(
  alpha: Uint8Array,
  width: number,
  height: number,
  params: BevelEmbossParams,
): BevelEmbossResult {
  const smoothed = boxBlurAlpha(alpha, width, height, Math.max(1, params.size));
  const angleRad = (params.angle * Math.PI) / 180;
  const altitudeRad = (params.altitude * Math.PI) / 180;
  // 只取光源方向的水平分量——斜面浮雕要的是"跟平面基准的偏差"，不是绝对
  // 光照强度：平面区域（梯度为 0）必须恒为 0，不然整块图形会被一层均匀的
  // 高光/阴影蒙上（第一版量出来的真实 bug，用 revert-and-confirm 抓到的）。
  // 光源高度角只用来调制水平分量的强度——高度角越低（光线越贴着表面斜射），
  // 同样的梯度产生的明暗对比越强，这是"高度角"这个参数该有的效果。
  const lightX = Math.cos(angleRad) * Math.cos(altitudeRad);
  const lightY = Math.sin(angleRad) * Math.cos(altitudeRad);

  const highlight = new Uint8Array(alpha.length);
  const shadow = new Uint8Array(alpha.length);
  const at = (x: number, y: number): number => smoothed[Math.min(height - 1, Math.max(0, y)) * width + Math.min(width - 1, Math.max(0, x))]!;

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = y * width + x;
      // 用原始 alpha（不是模糊过的）判断"在图形内部"——斜面浮雕是贴在
      // 图层自己表面上的明暗，不该像投影/发光那样往外扩散到 alpha=0 的地方；
      // 模糊只用来算梯度，不该改变效果的作用范围
      if (alpha[i]! <= 0) continue;
      // 中心差分近似梯度，值域约 [-255,255]；除以 128 粗略归一化到强度量级
      const gx = (at(x + 1, y) - at(x - 1, y)) / 2 / 128;
      const gy = (at(x, y + 1) - at(x, y - 1)) / 2 / 128;
      const intensity = gx * lightX + gy * lightY; // 平面区域 gx=gy=0 ⇒ 恒为 0
      if (intensity > 0) highlight[i] = Math.round(Math.min(1, intensity) * 255);
      else shadow[i] = Math.round(Math.min(1, -intensity) * 255);
    }
  }
  return { highlight, shadow };
}

export interface SatinParams {
  readonly distance: number;
  readonly blur: number;
}

/**
 * 光泽：内部若干条等距离环（形态学距离场的粗近似）叠加正弦调制，
 * 模拟真实 PS 那种"内部扭曲光泽等高线"的手感——不是精确的距离变换，
 * 是本次新增效果里第二个已知近似（第一个是斜面和浮雕）。
 */
export function renderSatin(alpha: Uint8Array, width: number, height: number, params: SatinParams): Uint8Array {
  const step = Math.max(1, Math.round(params.distance / 3));
  const out = new Uint8Array(alpha.length);
  let previous = alpha;
  for (let ring = 1; ring <= 3; ring++) {
    const eroded = erodeAlpha(alpha, width, height, step * ring);
    const band = subtractAlpha(previous, eroded);
    const phase = (Math.sin(ring * 1.3) + 1) / 2; // 让相邻环强度有起伏，避免线性单调
    for (let i = 0; i < out.length; i++) out[i] = Math.max(out[i]!, Math.round(band[i]! * phase));
    previous = eroded;
  }
  return boxBlurAlpha(out, width, height, params.blur);
}
